/**
 * The events one task owed its receiver, in the order they were announced.
 *
 * Read from the per-task route, so the rows come back by sequence rather
 * than by time: a status event still retrying sits above the output event
 * that already landed, which is the ordering rule made visible. Retry is
 * offered on a failed row only, the same guard the fleet-wide list keeps.
 */
import { ApiError } from '@optum/api-client/client';
import {
  listTaskWebhookDeliveries,
  retryWebhookDelivery,
} from '@optum/api-client/endpoints/webhook-deliveries';
import type { WebhookDelivery } from '@optum/api-client/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { Button } from '../../ui/Button.js';
import { useToast } from '../../ui/useToast.js';
import { absoluteDateTime } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { DataTable } from '../../ui/DataTable.js';
import { AnnouncedStatus, DeliveryStatusBadge, EventBadge } from '../webhooks/delivery-badges.js';

export function TaskDeliveries({ taskId }: { taskId: string }) {
  const query = useQuery({
    queryKey: qk.taskWebhookDeliveries(taskId),
    queryFn: () => listTaskWebhookDeliveries(taskId),
  });

  if (query.isPending) return <LoadingBlock />;
  if (query.isError) {
    return (
      <ErrorBlock message={query.error instanceof Error ? query.error.message : 'Failed to load'} />
    );
  }
  const rows = query.data ?? [];
  if (rows.length === 0) {
    return <EmptyBlock>No endpoint received this task.</EmptyBlock>;
  }

  return (
    <DataTable
      rows={rows}
      columns={[
        { key: 'sequence', label: 'Seq', mono: true },
        {
          key: 'event',
          label: 'Event',
          render: (delivery) => <EventBadge event={delivery.event} />,
        },
        {
          key: 'announced_status',
          label: 'Announced',
          render: (delivery) => <AnnouncedStatus value={delivery.announced_status} />,
        },
        {
          key: 'status',
          label: 'Delivery',
          render: (delivery) => <DeliveryStatusBadge status={delivery.status} />,
        },
        { key: 'attempts', label: 'Attempts', mono: true },
        {
          key: 'last',
          label: 'Last result',
          wrap: true,
          render: (delivery) => (
            <>
              {delivery.last_status_code ?? '—'}
              {delivery.last_error ? ` · ${delivery.last_error.slice(0, 60)}` : ''}
            </>
          ),
        },
        {
          key: 'when',
          label: 'When',
          mono: true,
          render: (delivery) => absoluteDateTime(delivery.delivered_at ?? delivery.created_at),
        },
        {
          key: 'retry',
          label: '',
          render: (delivery) => <RetryCell taskId={taskId} delivery={delivery} />,
        },
      ]}
    />
  );
}

function RetryCell({ taskId, delivery }: { taskId: string; delivery: WebhookDelivery }) {
  const qc = useQueryClient();
  const toast = useToast();
  const retry = useMutation({
    mutationFn: () => retryWebhookDelivery(delivery.id),
    onSuccess: () => {
      toast.success('Delivery requeued');
      void qc.invalidateQueries({ queryKey: qk.taskWebhookDeliveries(taskId) });
    },
    onError: (e) => toast.error('Retry failed', e instanceof ApiError ? e.message : String(e)),
  });

  // Only a failed row can be retried; the API refuses the rest.
  if (delivery.status !== 'failed') return null;
  return (
    <Button size="sm" variant="secondary" onClick={() => retry.mutate()} disabled={retry.isPending}>
      {retry.isPending ? 'Retrying…' : 'Retry'}
    </Button>
  );
}
